import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Bot, Globe, Megaphone, Share2, Users, Zap, Target, BarChart, CheckCircle } from 'lucide-react';
import Button from '../components/ui/Button';
import { supabase } from '../lib/supabase';
import type { Service } from '../types';

const iconMap: Record<string, React.ElementType> = {
  'AI Automation': Bot,
  'Web Development': Globe,
  'Advertising': Megaphone,
  'Social Media Marketing': Share2,
  'Lead Generation': Users,
  'Data Analytics': BarChart,
  'Performance Optimization': Zap,
  'Market Research': Target
};

const packages = {
  'AI Automation': [
    { name: 'Starter', price: 499, features: ['Basic AI Integration', 'Automated Workflows', 'Monthly Reports'] },
    { name: 'Professional', price: 999, features: ['Advanced AI Models', 'Custom Workflows', 'Weekly Reports', 'Priority Support'] },
    { name: 'Enterprise', price: 1999, features: ['Custom AI Solutions', '24/7 Support', 'Daily Reports', 'Dedicated Manager'] }
  ],
  'Web Development': [
    { name: 'Basic', price: 999, features: ['5 Pages', 'Responsive Design', 'Basic SEO'] },
    { name: 'Business', price: 1999, features: ['10 Pages', 'Advanced SEO', 'E-commerce Integration', 'Custom Features'] },
    { name: 'Premium', price: 3999, features: ['Unlimited Pages', 'Full E-commerce', 'Custom Development', 'Priority Support'] }
  ],
  'Advertising': [
    { name: 'Starter', price: 799, features: ['Social Media Ads', 'Basic Analytics', 'Monthly Strategy'] },
    { name: 'Growth', price: 1499, features: ['Multi-Platform Ads', 'Advanced Analytics', 'Weekly Strategy', 'A/B Testing'] },
    { name: 'Scale', price: 2999, features: ['Full-Service Ads', 'Custom Analytics', 'Daily Optimization', 'Dedicated Strategist'] }
  ],
  'Social Media Marketing': [
    { name: 'Essential', price: 599, features: ['3 Platforms', '12 Posts/Month', 'Basic Engagement'] },
    { name: 'Professional', price: 1299, features: ['5 Platforms', '20 Posts/Month', 'Advanced Engagement', 'Content Calendar'] },
    { name: 'Elite', price: 2499, features: ['All Platforms', 'Unlimited Posts', 'Full Management', 'Strategy Sessions'] }
  ],
  'Lead Generation': [
    { name: 'Starter', price: 699, features: ['Email Campaigns', 'Landing Pages', 'Basic CRM'] },
    { name: 'Advanced', price: 1399, features: ['Multi-Channel', 'Custom Landing Pages', 'Advanced CRM', 'Lead Scoring'] },
    { name: 'Premium', price: 2799, features: ['Full-Service', 'Marketing Automation', 'Enterprise CRM', 'Sales Integration'] }
  ],
  'Data Analytics': [
    { name: 'Basic', price: 899, features: ['Monthly Reports', 'Basic Metrics', 'Data Visualization'] },
    { name: 'Pro', price: 1799, features: ['Weekly Reports', 'Custom Metrics', 'Advanced Analytics', 'Trend Analysis'] },
    { name: 'Enterprise', price: 3499, features: ['Real-time Analytics', 'AI Insights', 'Custom Dashboard', 'Strategy Consulting'] }
  ],
  'Performance Optimization': [
    { name: 'Standard', price: 599, features: ['Speed Optimization', 'Basic SEO', 'Monthly Audits'] },
    { name: 'Advanced', price: 1299, features: ['Full Optimization', 'Technical SEO', 'Weekly Audits', 'Security Hardening'] },
    { name: 'Complete', price: 2499, features: ['Custom Solutions', 'Enterprise SEO', 'Daily Monitoring', '24/7 Support'] }
  ],
  'Market Research': [
    { name: 'Basic', price: 799, features: ['Market Analysis', 'Competitor Research', 'Monthly Insights'] },
    { name: 'Professional', price: 1599, features: ['Deep Analysis', 'Industry Trends', 'Weekly Updates', 'Custom Reports'] },
    { name: 'Enterprise', price: 2999, features: ['Full Research Suite', 'Real-time Monitoring', 'Strategy Planning', 'Consulting'] }
  ]
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { 
    opacity: 0,
    y: 20,
    scale: 0.95
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 20
    }
  }
};

export default function Services() {
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [selectedPackage, setSelectedPackage] = useState<string | null>(null);
  const [timePeriod, setTimePeriod] = useState<number>(3); // months
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
    fetchServices();
  }, []);

  async function checkAuth() {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      navigate('/login');
      return;
    }
  }

  async function fetchServices() {
    try {
      setError(null);
      const { data, error: fetchError } = await supabase
        .from('services')
        .select('*')
        .order('name');

      if (fetchError) throw fetchError;
      setServices(data || []);
    } catch (error) {
      console.error('Error fetching services:', error);
      setError('Failed to load services. Please try again later.');
    } finally {
      setLoading(false);
    }
  }

  const handleServiceSelect = (service: Service) => {
    setSelectedService(service);
    setSelectedPackage(null); // Reset package selection
  };

  const handlePackageSelect = (packageName: string) => {
    setSelectedPackage(packageName);
  };

  const handleContinue = () => {
    if (selectedService && selectedPackage) {
      navigate(`/services/${selectedService.id}`, {
        state: {
          selectedPackage,
          timePeriod
        }
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full"
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Oops!</h2>
          <p className="text-gray-600 mb-8">{error}</p>
          <Button onClick={() => fetchServices()}>Try Again</Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Our Services</h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Choose from our range of professional services tailored to help your business grow
          </p>
        </motion.div>

        {/* Services Grid */}
        {!selectedService ? (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid gap-6 md:grid-cols-2 lg:grid-cols-3"
          >
            {services.map((service) => {
              const Icon = iconMap[service.name] || Bot;
              const servicePackages = packages[service.name as keyof typeof packages] || [];
              const startingPrice = servicePackages.length > 0 ? Math.min(...servicePackages.map(pkg => pkg.price)) : 0;

              return (
                <motion.div
                  key={service.id}
                  variants={itemVariants}
                  whileHover={{ y: -5, scale: 1.02 }}
                  className="group bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden"
                  onClick={() => handleServiceSelect(service)}
                >
                  <div className="p-8">
                    <div className="flex items-center justify-between mb-6">
                      <motion.div
                        whileHover={{ rotate: 15 }}
                        className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center group-hover:bg-blue-100 transition-colors duration-300"
                      >
                        <Icon className="w-8 h-8 text-blue-600" />
                      </motion.div>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors duration-300">
                      {service.name}
                    </h3>
                    <p className="text-gray-600 mb-6 line-clamp-3">{service.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-sm font-medium group-hover:bg-blue-100 transition-colors duration-300">
                        View Packages
                      </span>
                      {startingPrice > 0 && (
                        <span className="text-sm text-gray-500">
                          From ${startingPrice}/mo
                        </span>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-8"
          >
            {/* Selected Service Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <motion.div
                  whileHover={{ rotate: 15 }}
                  className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center"
                >
                  {iconMap[selectedService.name] && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 200 }}
                    >
                      {React.createElement(iconMap[selectedService.name], {
                        className: "w-8 h-8 text-blue-600"
                      })}
                    </motion.div>
                  )}
                </motion.div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{selectedService.name}</h2>
                  <p className="text-gray-600">{selectedService.description}</p>
                </div>
              </div>
              <Button
                variant="outline"
                onClick={() => setSelectedService(null)}
              >
                Change Service
              </Button>
            </div>

            {/* Packages */}
            <div className="grid md:grid-cols-3 gap-8">
              {(packages[selectedService.name as keyof typeof packages] || []).map((pkg, index) => (
                <motion.div
                  key={pkg.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`bg-white rounded-2xl p-6 ${
                    selectedPackage === pkg.name
                      ? 'ring-2 ring-blue-600 shadow-lg'
                      : 'shadow-sm hover:shadow-md'
                  } cursor-pointer transition-all duration-200`}
                  onClick={() => handlePackageSelect(pkg.name)}
                >
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-xl font-semibold text-gray-900">{pkg.name}</h3>
                    {selectedPackage === pkg.name && (
                      <CheckCircle className="w-6 h-6 text-blue-600" />
                    )}
                  </div>
                  <div className="text-3xl font-bold text-gray-900 mb-4">
                    ${pkg.price}
                    <span className="text-lg text-gray-500 font-normal">/mo</span>
                  </div>
                  <ul className="space-y-3 mb-6">
                    {pkg.features.map((feature, i) => (
                      <li key={i} className="flex items-center text-gray-600">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>

            {/* Time Period Selection */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Time Period</h3>
              <div className="flex gap-4">
                {[3, 6, 12].map((months) => (
                  <button
                    key={months}
                    onClick={() => setTimePeriod(months)}
                    className={`flex-1 p-4 rounded-xl border-2 transition-all duration-200 ${
                      timePeriod === months
                        ? 'border-blue-600 bg-blue-50 text-blue-600'
                        : 'border-gray-200 hover:border-blue-200'
                    }`}
                  >
                    <div className="text-2xl font-bold mb-1">{months}</div>
                    <div className="text-sm">Months</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Continue Button */}
            <div className="flex justify-end">
              <Button
                onClick={handleContinue}
                disabled={!selectedPackage}
                className="px-8"
              >
                Continue
              </Button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}